<template>
  <header class="core-header">
    <nav class="core-header-main">
      <div class="core-wrapper h-full w-full">
        <div class="flex h-full items-center justify-center">
          <button
            v-for="item of navigation"
            class="core-project-nav-link"
            :key="item.name"
            @click="goToTab(item.name)"
            :class="{ active: route.name === item.name }">
            <span class="core-project-nav-link-label hidden sm:inline-flex">{{ item.title }}</span>
            <span class="core-project-nav-link-label inline-flex sm:hidden">{{ item.abr }}</span>
          </button>
        </div>
      </div>
    </nav>
    <template v-if="props.hasTabBar">
      <div class="core-header-separator"></div>
      <div class="core-tab-bar">
        <slot name="tabBar" />
      </div>
    </template>
  </header>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import type { DefaultObject } from '@/types'
import { useWidgetText } from '@/helpers/useWidgetText'
import { useProjectStore } from '@/stores/project'
const route = useRoute()
const router = useRouter()
const projectStore = useProjectStore()

const { t } = useI18n()
const { wt: wtGeneral } = useWidgetText('widget-texts-general')
const props = defineProps<{
  meta?: DefaultObject
  hasTabBar?: boolean
}>()

const navigation = computed(() => {
  const navigation = [
    {
      name: 'summary-project-view',
      title: wtGeneral('navigation', 'summary'),
      abr: t('general.navigation.project.abr_summary'),
    },
  ]
  if (props.meta?.roles?.length) {
    navigation.splice(0, 0, {
      name: 'service-project-view',
      title: wtGeneral('navigation', 'service'),
      abr: t('general.navigation.project.abr_service'),
    })
    navigation.splice(0, 0, {
      name: 'setup-project-view',
      title: wtGeneral('navigation', 'setup'),
      abr: t('general.navigation.project.abr_setup'),
    })
  }
  if (props.meta?.canEdit) {
    navigation.splice(0, 0, {
      name: 'general-project-view',
      title: wtGeneral('navigation', 'general'),
      abr: t('general.navigation.project.abr_general'),
    })
  }
  return navigation
})

const goToTab = (routeName: string) => {
  /* removed router-link to disable open in new Tab function */
  const roleIndex = projectStore.getValidRoles.indexOf(projectStore.getActiveProjectRole ?? '')
  const params =
    roleIndex !== -1 && (routeName === 'setup-project-view' || routeName === 'service-project-view')
      ? { currentTabIndex: roleIndex }
      : undefined

  router.push({ name: routeName, params })
}
</script>

<style lang="postcss" scoped>
.core-header {
  @apply fixed top-0 left-0 right-0 z-60;
  @apply bg-white border-b border-neutral-300;
  box-shadow:
    0 2px 4px rgba(0, 0, 0, 0.05),
    0 8px 16px -10px rgba(0, 0, 0, 0.09);
}

.core-header-main {
  @apply h-headerHeight bg-white;
}

.core-header-separator {
  height: 1px;
  background-color: rgb(212 212 212 / 1);
  position: relative;
  z-index: 1;
  box-shadow:
    0 1px 2px rgba(0, 0, 0, 0.08),
    0 4px 8px -6px rgba(0, 0, 0, 0.12);
}

.core-tab-bar {
  background-color: #fdfdfd;
}

.core-project-nav-link {
  --nav-active-offset: 0.25rem;
  @apply relative h-full px-2 md:mx-4 md:px-4 lg:w-40;
  @apply flex items-center justify-center;
  @apply border-0 bg-transparent text-neutral-550;
  @apply transition-colors duration-150 ease-in-out;
  @apply hover:text-primary-medium;
  @apply appearance-none rounded-none;
  @apply focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-default focus-visible:ring-offset-1;
  transform: translateY(0);

  &::after {
    content: '';
    @apply absolute left-0 right-0 h-0.5 bg-transparent;
    bottom: 0.2rem;
  }

  &.active {
    @apply text-primary-dark;
    transform: translateY(var(--nav-active-offset));
  }

  &.active::after {
    @apply bg-primary-dark;
    transform: translateY(calc(var(--nav-active-offset) * -1));
  }
}

.core-project-nav-link-label {
  @apply items-center leading-none;
  transform: translateY(0);
}

.core-project-nav-link.active .core-project-nav-link-label {
  transform: translateY(calc(var(--nav-active-offset) * -1));
}
</style>
